import { DataTable } from '@/global/components/DataGrid/components/DataTable'
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/global/components/ui/breadcrumb'
import { cn } from '@/lib/utils'
import { onlyDefined } from '@acme/common'
import React, { HTMLProps, ReactNode, memo } from 'react'
import FiltrosPadrao from './Filtros'

interface SimpleContainerProps {
    children: ReactNode
    className?: string
}

/** div estilizado
 *
 * @example
 * <ContentGuide>
 *   <ContentGuide.Card>
 *     <ContentGuide.Header label={['Home', { title: 'Section', to: '/section' }, 'Current Page']} />
 *     <ContentGuide.DataGrid {...props} />
 *   </ContentGuide.Card>
 * </ContentGuide>
 */
export function ContentGuide({ children, ...props }: SimpleContainerProps) {
    return (
        <div
            className={cn(
                'flex flex-col items-center justify-center',
                'overflow-hidden',
                'h-[calc(100vh_-_4rem)]',
                'p-0 pt-0.5 sm:py-3 sm:px-4',
                'dark:bg-background',
                props.className,
            )}
            {...props}
        >
            {children}
        </div>
    )
}

function CardGuide({ children, className }: SimpleContainerProps) {
    return (
        <div
            className={cn(
                'size-full max-w-[1500px] flex gap-1',
                'flex-col justify-center items-center',
                'overflow-hidden p-3 sm:rounded-sm rounded-none',
                'rounded-lg shadow-lg ring-1 ring-accent',
                'bg-foreground',
                className,
            )}
        >
            {children}
        </div>
    )
}

type Label = (string | { title: string; to: string } | React.ReactNode)[]

interface HeaderGuideProps extends Omit<HTMLProps<HTMLDivElement>, 'label'> {
    label: Label
}

function HeaderGuide({ children, label, ...props }: HeaderGuideProps) {
    return (
        <div
            className={cn(
                'w-full flex justify-between items-center',
                'h-17 overflow-x-auto overflow-y-clip scrollbar-minimal',
                props.className,
            )}
        >
            <Breadcrumb>
                <BreadcrumbList>
                    {label.filter(onlyDefined).map((item, index, arr) => {
                        const isLast = index === arr.length - 1,
                            separator = !isLast ? (
                                <BreadcrumbSeparator key={index + 's'} />
                            ) : null,
                            Comp = isLast ? BreadcrumbPage : BreadcrumbItem

                        if (!item) return null
                        else if (typeof item === 'string')
                            return [
                                <Comp key={index}>
                                    <span
                                        className={cn(
                                            'whitespace-nowrap hidden lg:block!',
                                            isLast ? 'block!' : '',
                                        )}
                                    >
                                        {item}
                                    </span>
                                    {!isLast && (
                                        <span className="block lg:hidden">
                                            ...
                                        </span>
                                    )}
                                </Comp>,
                                separator,
                            ]
                        else if (
                            item instanceof Object &&
                            'title' in item &&
                            'to' in item
                        )
                            return [
                                <Comp key={index}>
                                    <BreadcrumbLink href={item.to}>
                                        {item.title}
                                    </BreadcrumbLink>
                                </Comp>,
                                separator,
                            ]
                        else
                            return [
                                <Comp key={index}>
                                    <span>{item}</span>
                                </Comp>,
                                separator,
                            ]
                    })}
                </BreadcrumbList>
            </Breadcrumb>
            <div className="flex gap-1" {...props}>
                {children}
            </div>
        </div>
    )
}

/** div estilizado
 * @example
 * <ContentGuide.Card>
 *   <p>Conteúdo do card</p>
 * </ContentGuide.Card>
 */
ContentGuide.Card = memo(CardGuide)
/** div com breadcrumbs
 *
 * @example
 * <ContentGuide.Header label={['Home', { title: 'Section', to: '/section' }, 'Current Page']} />
 */
ContentGuide.Header = memo(HeaderGuide)

ContentGuide.DataGrid = DataTable
ContentGuide.Filtros = memo(FiltrosPadrao)

export default ContentGuide
